import * as THREE from 'three';
import { IsoCamera } from './IsoCamera';
import { Input } from './Input';
import { Loop, type LoopCallbacks } from './Loop';

/**
 * Renderer, scene, camera en invoer bij elkaar. De Engine weet niets van gameplay —
 * hij levert de bak waarin de wereld draait en houdt hem op de juiste maat.
 */

export interface EngineStats {
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
}

export class Engine {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly isoCamera: IsoCamera;
  readonly input: Input;
  readonly loop: Loop;

  readonly #container: HTMLElement;
  readonly #onResize: () => void;

  constructor(container: HTMLElement, callbacks: LoopCallbacks) {
    this.#container = container;

    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
    });
    // Boven 2 levert de extra scherpte niets op tegen forse fill-rate-kosten.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // ACES is gemaakt voor HDR-film met een groot highlight-bereik en heeft een stevige toe
    // die lage waarden naar zwart trekt. Deze nachtstad leeft vrijwel volledig in dat lage
    // bereik, dus juist daar werd alles dichtgeknepen: verhoog je de lampen, dan eet de curve
    // de winst weer op. Khronos PBR Neutral houdt de kleur intact en rolt highlights netjes
    // af, maar laat de schaduwen staan.
    this.renderer.toneMapping = THREE.NeutralToneMapping;
    // De belichting zelf staat in VisualSettings, onder de helderheidsschuif.
    this.renderer.toneMappingExposure = 1;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    this.scene.name = 'NA_World';

    this.isoCamera = new IsoCamera();
    this.input = new Input(this.renderer.domElement);
    this.loop = new Loop(callbacks);

    this.#onResize = () => this.resize();
    window.addEventListener('resize', this.#onResize);
    this.resize();
  }

  get camera(): THREE.OrthographicCamera {
    return this.isoCamera.camera;
  }

  get viewportHeight(): number {
    return this.#container.clientHeight;
  }

  resize(): void {
    const width = this.#container.clientWidth;
    const height = this.#container.clientHeight;
    if (width === 0 || height === 0) return;
    this.renderer.setSize(width, height, false);
    this.isoCamera.resize(width, height);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  stats(): EngineStats {
    const { render, memory } = this.renderer.info;
    return {
      drawCalls: render.calls,
      triangles: render.triangles,
      geometries: memory.geometries,
      textures: memory.textures,
    };
  }

  dispose(): void {
    this.loop.stop();
    window.removeEventListener('resize', this.#onResize);
    this.input.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
